/** One shown view: paint the kept data, then fetch in the background when it is stale, old or missing (rule U-36). */

import { currentScope, note, whenOpen } from "./store";
import { subscribeSync, syncStatus } from "./sync";
import { FALLBACK_POLL_MS, fetchFailed, fetching, isStale, keepView, keptView, lastFetch, MAX_AGE_MS, minRefetchMs, mount, STALE_MIN_MS, subscribeViews, baseOf } from "./views";

/** The browser seams; the tests change them. */
export const runnerDeps = {
  now: () => Date.now(),
  visible: () => typeof document === "undefined" || document.visibilityState !== "hidden",
};

export interface RunState {
  /** A fetch runs. */
  checking: boolean;
  /** A fetch ended with no error on this visit, or the kept view is current. */
  checked: boolean;
  /** The error of the last fetch, or null. */
  error: unknown;
}

export interface Runner {
  state: () => RunState;
  subscribe: (listener: () => void) => () => void;
  reload: (reason?: string) => void;
  stop: () => void;
}

const errorName = (e: unknown) => (e && typeof e === "object" && "status" in e ? `http_${String((e as { status: unknown }).status)}` : e instanceof Error ? e.name : "unknown");

/** The length of the JSON of an answer, as the size of the response. */
export function sizeOf(data: unknown): number {
  try {
    return JSON.stringify(data)?.length ?? 0;
  } catch {
    return 0;
  }
}

/** Why the view must fetch at the visit, or null when the kept view is current. */
export function openReason(kept: { at: number } | null, stale: boolean, fallback: boolean, fetched: number | null, now: number): string | null {
  if (!kept) return "miss";
  if (stale) return "stale";
  if (fallback) return "no_feed";
  if (fetched === null) return "first";
  if (now - kept.at > MAX_AGE_MS) return "age";
  return null;
}

/** Why the view `key` must fetch now, or null when its kept view is current. */
export const reasonOf = (key: string) => openReason(keptView(key), isStale(key), syncStatus().mode === "fallback", lastFetch(key), runnerDeps.now());

/** The state before the first fetch: a current kept view counts as checked. */
export const openState = (key: string): RunState => ({ checking: false, checked: reasonOf(key) === null, error: null });

export function runView<T>(key: string, load: (signal: AbortSignal) => Promise<T>): Runner {
  const view = baseOf(key);
  const minMs = minRefetchMs(key);
  const reasonNow = () => reasonOf(key);
  let state: RunState = openState(key);
  let stopped = false;
  let control: AbortController | null = null;
  let timer: ReturnType<typeof setTimeout> | undefined;
  const listeners = new Set<() => void>();
  const set = (change: Partial<RunState>) => {
    state = { ...state, ...change };
    for (const listener of listeners) listener();
  };

  async function fetchNow(reason: string): Promise<void> {
    if (stopped || control) return;
    clearTimeout(timer);
    timer = undefined;
    const ctl = new AbortController();
    control = ctl;
    const at = runnerDeps.now();
    const scope = currentScope();
    let moved = false;
    fetching(key, at);
    set({ checking: true });
    const started = performance.now();
    try {
      const data = await load(ctl.signal);
      if (ctl.signal.aborted) return;
      moved = currentScope() !== scope;
      if (moved) return;
      keepView(key, data, at);
      const ms = Math.round(performance.now() - started);
      const bytes = sizeOf(data);
      note("view_fetch", `view: ${view} fetched (${reason}) in ${ms} ms, ${bytes} bytes`, { view, reason, ms, bytes });
      set({ checking: false, checked: true, error: null });
    } catch (e) {
      if (ctl.signal.aborted) return;
      moved = currentScope() !== scope;
      if (moved) return;
      fetchFailed(key);
      note("view_fetch_failed", `view: ${view} fetch failed (${reason}): ${errorName(e)}`, { view, reason, error: errorName(e) });
      set({ checking: false, error: e });
    } finally {
      if (control === ctl) control = null;
      if (moved) {
        note("view_dropped", `view: ${view} answer dropped, the scope changed during the fetch`, { view, reason: "scope_change" });
        set({ checking: false });
        if (currentScope() !== null) void fetchNow("scope_change");
      } else plan();
    }
  }

  function tick(reason: string) {
    timer = undefined;
    if (stopped) return;
    if (!runnerDeps.visible()) {
      timer = setTimeout(() => tick(reason), STALE_MIN_MS);
      return;
    }
    void fetchNow(reason);
  }

  /** Set the one timer of the next fetch: the old poll on an API with no feed, a stale view, or an old view. */
  function plan() {
    clearTimeout(timer);
    timer = undefined;
    if (stopped || control) return;
    const now = runnerDeps.now();
    const last = lastFetch(key) ?? 0;
    if (syncStatus().mode === "fallback") {
      timer = setTimeout(() => tick("poll"), Math.max(0, last + FALLBACK_POLL_MS - now));
      return;
    }
    if (isStale(key)) {
      timer = setTimeout(() => tick("stale"), Math.max(0, last + minMs - now));
      return;
    }
    const kept = keptView(key);
    if (kept) timer = setTimeout(() => tick("age"), Math.max(minMs, kept.at + MAX_AGE_MS - now));
  }

  const unmount = mount(key);
  const unViews = subscribeViews(plan);
  const unSync = subscribeSync(plan);
  const opened = performance.now();
  void whenOpen().then(() => {
    if (stopped) return;
    const kept = keptView<T>(key);
    const now = runnerDeps.now();
    const reason = reasonNow();
    const ms = Math.round(performance.now() - opened);
    if (kept) note("view_hit", `view: ${view} hit, ${Math.round((now - kept.at) / 1_000)} s old, ${reason ?? "current"}`, { view, age_s: Math.round((now - kept.at) / 1_000), reason, ms });
    else note("view_miss", `view: ${view} miss`, { view, ms });
    if (reason) void fetchNow(reason);
    else {
      set({ checked: true });
      plan();
    }
  });

  return {
    state: () => state,
    subscribe: (listener) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    reload: (reason = "reload") => void fetchNow(reason),
    stop: () => {
      stopped = true;
      clearTimeout(timer);
      control?.abort();
      control = null;
      unViews();
      unSync();
      unmount();
    },
  };
}
